import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  List,
  Progress,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import crmStandupService from '@/services/crmStandup.service';
import { formatDate } from '@/utils/format/date';

const { Title, Text, Paragraph } = Typography;

const StandupRepPage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const canManage = Boolean(
    user?.is_super_admin || user?.permissions?.includes('crm.standup.manage')
  );
  const [date, setDate] = useState<Dayjs>(dayjs());
  const dateStr = date.format('YYYY-MM-DD');
  const [form] = Form.useForm();

  const { data, isLoading, error } = useQuery({
    queryKey: ['crm-standup-me', dateStr],
    queryFn: () => crmStandupService.myBrief(dateStr),
  });

  const { data: spi } = useQuery({
    queryKey: ['spi-me', dateStr],
    queryFn: () => crmStandupService.spiMe(),
    retry: false,
  });

  useEffect(() => {
    if (!data?.questions) return;
    const values: Record<string, string> = {};
    for (const q of data.questions) {
      const existing = data.answers?.find((a) => a.question_key === q.key);
      values[q.key] = existing?.answer_text || '';
    }
    form.setFieldsValue(values);
  }, [data, form]);

  const submitMut = useMutation({
    mutationFn: async (values: Record<string, string>) => {
      if (!data?.meeting?.id) throw new Error('No meeting');
      const answers = (data.questions || []).map((q) => ({
        question_key: q.key,
        answer_text: values[q.key]?.trim() || null,
      }));
      return crmStandupService.submitAnswers(data.meeting.id, answers);
    },
    onSuccess: () => {
      message.success('Stand-up updates submitted');
      qc.invalidateQueries({ queryKey: ['crm-standup-me', dateStr] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const brief = data?.brief;
  const continuity = data?.continuity;
  const finalized = data?.meeting?.status === 'Finalized';

  const spiPace = useMemo(() => {
    const pace = (spi as any)?.pace || (spi as any)?.on_track_status;
    const pct = Number((spi as any)?.won_progress_pct ?? (spi as any)?.progress_pct ?? NaN);
    return { pace, pct: Number.isFinite(pct) ? pct : null, label: (spi as any)?.period_label };
  }, [spi]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Daily Sales Stand-Up
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Auto-brief from CRM follow-ups, calendar, and pipeline — then answer the eight questions.
          </Paragraph>
        </Col>
        <Col>
          <Space wrap>
            <DatePicker value={date} onChange={(d) => d && setDate(d)} allowClear={false} />
            {canManage && (
              <>
                <Button>
                  <Link to={`/growth/standup/team?date=${dateStr}`}>Team console</Link>
                </Button>
                <Button>
                  <Link to="/growth/standup/history">History</Link>
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          showIcon
          message={(error as any)?.response?.data?.message || 'Could not load stand-up brief'}
        />
      )}

      {(spiPace.pace || spiPace.pct != null) && (
        <Card size="small" title="Sales targets (SPI)">
          <Space size="large" wrap>
            {spiPace.label && <Text type="secondary">{String(spiPace.label)}</Text>}
            {spiPace.pace && <Tag>{String(spiPace.pace)}</Tag>}
            {spiPace.pct != null && (
              <Progress percent={Math.round(spiPace.pct)} size="small" style={{ width: 180 }} />
            )}
            <Link to="/growth/sales-targets">Open targets</Link>
          </Space>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Activities yesterday" value={brief?.counts.activities_yesterday ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Follow-ups done" value={brief?.counts.completed_follow_ups ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Open commitments" value={brief?.counts.commitments_open ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={isLoading}>
            <Statistic
              title="Overdue"
              value={brief?.counts.commitments_overdue ?? 0}
              valueStyle={{ color: (brief?.counts.commitments_overdue || 0) > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {continuity && (
        <Card size="small" title="Commitment continuity">
          <Space wrap>
            <Tag color="green">Completed {continuity.counts.completed}</Tag>
            <Tag>Still open {continuity.counts.still_open}</Tag>
            <Tag color="red">Overdue {continuity.counts.overdue}</Tag>
            <Tag color="orange">Postponed {continuity.counts.postponed}</Tag>
            {continuity.prior_meeting_date && (
              <Text type="secondary">Prior finalized: {formatDate(continuity.prior_meeting_date)}</Text>
            )}
          </Space>
          {!!continuity.postponed?.length && (
            <List
              size="small"
              style={{ marginTop: 12 }}
              dataSource={continuity.postponed}
              renderItem={(item) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Text>{item.commitment_text || item.id}</Text>
                    <Text type="secondary">
                      Rescheduled {item.postpone_count}× · original {item.original_follow_up_date || '—'} →{' '}
                      {item.follow_up_date || '—'}
                    </Text>
                  </Space>
                  <Link to={`/growth/follow-ups?activity=${item.id}`}>Open</Link>
                </List.Item>
              )}
            />
          )}
        </Card>
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Yesterday & pipeline signals" loading={isLoading}>
            <Paragraph type="secondary">From CRM records only — not invented narrative.</Paragraph>
            <List
              size="small"
              header="Activities"
              dataSource={(brief?.activities_yesterday || []).slice(0, 8)}
              locale={{ emptyText: 'No activities yesterday' }}
              renderItem={(a: any) => (
                <List.Item>
                  <Text>
                    {a.activity_type_code}: {a.subject || '—'}
                  </Text>
                </List.Item>
              )}
            />
            <List
              size="small"
              header="Stage moves"
              dataSource={(brief?.opportunity_stage_moves || []).slice(0, 6)}
              locale={{ emptyText: 'No stage moves' }}
              renderItem={(a: any) => (
                <List.Item>
                  <Space>
                    <Link to={`/growth/opportunities/${a.opportunity_id}`}>
                      {a.opportunity_number || a.opportunity_id}
                    </Link>
                    <Text type="secondary">
                      {a.old_stage} → {a.new_stage}
                    </Text>
                  </Space>
                </List.Item>
              )}
            />
            <List
              size="small"
              header="Today’s calendar"
              dataSource={(brief?.today_calendar || []).slice(0, 8)}
              locale={{ emptyText: 'Nothing on calendar today' }}
              renderItem={(e: any) => (
                <List.Item>
                  <Text>{e.title || e.entry_type}</Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="My updates"
            loading={isLoading}
            extra={
              data?.participant?.submitted_at ? (
                <Tag color="blue">Submitted {formatDate(data.participant.submitted_at)}</Tag>
              ) : (
                <Tag>Not submitted</Tag>
              )
            }
          >
            {finalized && <Alert type="info" showIcon message="This meeting is finalized — answers are read-only." style={{ marginBottom: 12 }} />}
            <Form
              form={form}
              layout="vertical"
              onFinish={(values) => submitMut.mutate(values)}
              disabled={finalized}
            >
              {(data?.questions || []).map((q) => (
                <Form.Item key={q.key} name={q.key} label={q.label}>
                  <Input.TextArea rows={2} placeholder="Answer from CRM evidence…" />
                </Form.Item>
              ))}
              <Button type="primary" htmlType="submit" loading={submitMut.isPending} disabled={finalized}>
                Submit updates
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default StandupRepPage;
